// Modal: search and pick a PK patient for an Rx. Live search against
// GET /api/patients/search. Pick → POST /api/rx/{id}/patient. Or clear.

import { useEffect, useState } from "react";
import { apiGet, apiPost } from "../lib/api";
import type { PatientSearchResult } from "@shared/types";

type Props = {
  rxId: number;
  storeCode: string;
  currentPkId: number | null;
  onPicked: () => void;
  onCancel: () => void;
};

export function PatientPicker({ rxId, storeCode, currentPkId, onPicked, onCancel }: Props) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<PatientSearchResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      apiGet<PatientSearchResult[]>(`/patients/search?store=${storeCode}&q=${encodeURIComponent(q)}`, ctrl.signal)
        .then(setResults).catch((e) => setError(String(e)));
    }, 220);
    return () => { ctrl.abort(); clearTimeout(timer); };
  }, [q, storeCode]);

  async function pick(p: PatientSearchResult) {
    setBusy(true);
    try {
      await apiPost(`/rx/${rxId}/patient`, { patientPkId: p.patientPkId, storeId: p.storeId });
      onPicked();
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  }
  async function clear() {
    setBusy(true);
    try {
      await apiPost(`/rx/${rxId}/patient`, { clear: true });
      onPicked();
    } catch (e) { setError(String(e)); } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-surface rounded-lg shadow-xl w-[640px] max-h-[80vh] flex flex-col">
        <div className="px-5 py-4 border-b border-border flex items-center">
          <h2 className="text-lg font-semibold">Pick PK patient</h2>
          <span className="ml-auto text-xs text-ink-soft">Searching {storeCode}</span>
        </div>
        <div className="px-5 py-3 border-b border-border">
          <input
            autoFocus
            placeholder="Last name, or Last First, or Patient ID…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
        <div className="overflow-auto px-5 py-3 flex-1">
          {error && <div className="bg-bad-soft text-bad px-3 py-2 rounded mb-3 text-sm">{error}</div>}
          {results.length === 0 && q.trim().length >= 2 && (
            <div className="text-ink-soft text-sm py-4">No matches.</div>
          )}
          {q.trim().length < 2 && (
            <div className="text-ink-soft text-sm py-4">Type at least 2 characters to search.</div>
          )}
          {results.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {results.map((p) => (
                <li key={`${p.storeId}-${p.patientPkId}`} className="border border-border rounded p-3 hover:border-accent">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <strong className="text-sm">{p.lastName}, {p.firstName} {p.middleName ?? ""}</strong>
                        {p.patientPkId === currentPkId && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-good-soft text-good">currently linked</span>
                        )}
                        {p.species !== "HUMAN" && p.species !== "human" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#ecf6e7] text-[#4d7626]">{p.species}</span>
                        )}
                      </div>
                      <div className="text-xs text-ink-soft mt-1 flex flex-wrap gap-x-3">
                        {p.dob   && <span>DOB <strong className="text-ink">{p.dob}</strong></span>}
                        {p.phone && <span>{p.phone}</span>}
                        <span>Patient ID #{p.patientPkId}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => pick(p)}
                      disabled={busy}
                      className="px-3 py-1 bg-accent text-white rounded text-xs font-medium hover:brightness-95 disabled:opacity-40"
                    >
                      {p.patientPkId === currentPkId ? "Confirm" : "Pick"}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border flex items-center gap-2">
          {currentPkId !== null && (
            <button type="button" onClick={clear} disabled={busy}
              className="px-3 py-1.5 border border-bad text-bad rounded text-sm hover:bg-bad-soft disabled:opacity-40">
              Clear link
            </button>
          )}
          <div className="flex-1" />
          <button type="button" onClick={onCancel} disabled={busy}
            className="px-3 py-1.5 border border-border rounded text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}
